/* ---------- Hero: the claim beside the real product at work ---------- */
.hero {
  /* Same overlap as the home hero: cancel the flow space the sticky header reserves. */
  margin-top: calc(-1 * (var(--header-h) + 2 * var(--header-margin)));
  padding-block: calc(var(--header-h) + 2 * var(--header-margin) + var(--sp-12)) var(--sp-20);
  background: var(--surface-alt);
}
.heroGrid {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.heroText > * {
  max-width: 40rem;
}
.heroTitle {
  /* Room under the breadcrumb, which the dropped eyebrow label used to provide. */
  margin-top: var(--sp-6);
  font-size: clamp(2.4rem, 3.4vw + 1rem, 4.25rem);
  line-height: 1.04;
  letter-spacing: -0.035em;
  text-wrap: balance;
}
/* The two inputs carry the page's whole claim, so they are lit rather than merely
   coloured — the halo also keeps them legible over the moving scan behind. */
.input {
  color: var(--orange-400);
  text-shadow:
    0 0 15px rgba(248, 128, 0, 0.45),
    0 0 5px rgba(248, 128, 0, 0.35);
}
.heroLead {
  margin-top: var(--sp-5);
  font-size: var(--fs-lg);
  color: var(--text);
}
.heroActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--sp-4) var(--sp-8);
  margin-top: var(--sp-8);
}
.heroLink {
  font-weight: 600;
  color: var(--text-strong);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 0.3em;
}
.heroLink:hover {
  color: var(--accent-text);
}
.heroNote {
  margin: var(--sp-6) 0 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.heroNote + .heroNote {
  margin-top: var(--sp-2);
}
.heroMedia {
  position: relative;
}
.heroMedia > figure {
  box-shadow:
    0 0 0 1px var(--border),
    var(--shadow-lg);
}
/* One fact from the page, pinned to the footage like a readout. */
.heroChip {
  position: absolute;
  top: var(--sp-4);
  right: var(--sp-4);
  margin: 0;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius);
  background: rgba(8, 13, 19, 0.72);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: #f5f7fa;
  font-size: var(--fs-sm);
  font-weight: 600;
}
.heroChip::before {
  content: "";
  display: inline-block;
  width: 0.5rem;
  height: 0.5rem;
  margin-right: 0.5em;
  border-radius: 50%;
  background: #46d696;
  vertical-align: 0.05em;
}
@media (min-width: 64rem) {
  .heroGrid {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1.1fr);
    gap: var(--sp-16);
  }
}

/* ---------- Key benefits: six promises on hairline rows, three across ---------- */
.benefitList {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 19rem), 1fr));
  gap: var(--sp-10) var(--sp-12);
}
.benefit {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.benefitTitle {
  margin-bottom: var(--sp-3);
  font-size: var(--fs-xl);
  line-height: var(--lh-snug);
}
.benefit p {
  margin: 0;
  color: var(--text);
}
@media (min-width: 64rem) {
  .benefitList {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

/* ---------- The analyses story: ten steps, so a shorter screen of scroll each ---------- */
.analysesStory > ol > li {
  min-height: 88vh;
  min-height: 88svh;
}
@media (max-width: 47.99rem) {
  /* Ten steps under a readout: tighter type keeps the scene in view above each one. */
  .analysesStory {
    --story-title-size: 1.375rem;
    --story-text-size: 0.9375rem;
  }
}

/* ---------- How a run works: four numbered steps on hairline rows ---------- */
.run {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: var(--sp-8) var(--sp-10);
}
.run li {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.runNo {
  display: inline-grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  margin-bottom: var(--sp-4);
  border: 1px solid var(--accent);
  border-radius: 50%;
  font-weight: 700;
  color: var(--accent-text);
}
.run h3 {
  margin-bottom: var(--sp-2);
  font-size: var(--fs-lg);
}
.run p {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text);
}
@media (min-width: 64rem) {
  .run {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
}

/* ---------- Safety Dashboard: the real screenshot at full width, its figures docked ---------- */
.dash {
  /* Wider than the text column: the office photograph (2776px) holds up near full width. */
  width: min(96vw, 110rem);
  margin: 0 calc(50% - min(48vw, 55rem));
  overflow: hidden;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-lg);
  background: var(--surface-dark);
  box-shadow: var(--shadow-lg);
}
.dashImage {
  background: var(--surface-dark);
  /* A gradual vignette on every side: the office photograph fades into the figure's dark
     ground toward its edges, and the monitor at the centre stays at full strength. */
  mask-image: radial-gradient(ellipse 72% 78% at 50% 50%, #000 55%, transparent 100%);
  -webkit-mask-image: radial-gradient(ellipse 72% 78% at 50% 50%, #000 55%, transparent 100%);
}
.dashMetrics {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 10rem), 1fr));
  gap: var(--sp-5) var(--sp-6);
  padding: var(--sp-6) var(--sp-8);
  border-top: 1px solid var(--border);
}
.dashMetrics li {
  padding-left: var(--sp-4);
  border-left: 2px solid var(--accent);
}
.metricValue {
  margin: 0;
  font-size: var(--fs-2xl);
  font-weight: 650;
  letter-spacing: var(--tracking-heading);
  color: var(--text-on-dark);
}
.metricLabel {
  margin: 2px 0 0;
  font-size: var(--fs-sm);
  color: var(--text-on-dark-muted);
}
.metricNote {
  margin: 2px 0 0;
  font-size: 0.75rem;
  line-height: 1.5;
  color: var(--asphalt-400);
}
.dashCaption {
  max-width: 70rem;
  margin: 0;
  padding: 0 var(--sp-8) var(--sp-6);
  font-size: var(--fs-sm);
  color: var(--asphalt-400);
}
.dashBody {
  display: grid;
  gap: var(--sp-10);
  margin-top: var(--sp-16);
}
.dashLead {
  max-width: 46rem;
  margin: 0;
  font-size: var(--fs-lg);
  color: var(--text-strong);
}
.dashPoints {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 17rem), 1fr));
  gap: var(--sp-10) var(--sp-12);
}
.dashPoints li {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.dashPoints h3 {
  margin-bottom: var(--sp-2);
  font-size: var(--fs-lg);
}
.dashPoints p {
  margin: 0;
  color: var(--text);
}
.dashFoot {
  margin: var(--sp-12) 0 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
@media (min-width: 64rem) {
  .dashPoints {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
@media (max-width: 47.99rem) {
  /* Two across instead of one long column of six. */
  .dashMetrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    padding: var(--sp-5);
  }
  .metricValue {
    font-size: var(--fs-xl);
  }
  .dashCaption {
    padding: 0 var(--sp-5) var(--sp-5);
  }
}

/* ---------- Deliverables: by audience, one tab each ---------- */
.formats {
  display: flex;
  flex-wrap: wrap;
  gap: var(--sp-2);
  margin: calc(-1 * var(--sp-6)) 0 var(--sp-10);
}
.formats li {
  padding: var(--sp-1) var(--sp-3);
  border: 1px solid var(--border-strong);
  border-radius: 999px;
  font-size: var(--fs-sm);
  color: var(--text);
}
.deliverTabs {
  margin-bottom: var(--sp-8);
}
.deliverPanel {
  display: grid;
  gap: var(--sp-8);
}
.deliverMedia {
  margin: 0;
}
.deliverImage {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}
.deliverCaption {
  max-width: 46rem;
  margin-top: var(--sp-4);
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
/* A spec sheet: hairline rows, the file's name over what it is. */
.deliverList {
  display: grid;
  align-content: start;
}
.deliverList li {
  padding-block: var(--sp-4);
  border-top: 1px solid var(--border-strong);
  color: var(--text);
}
.deliverList li:last-child {
  border-bottom: 1px solid var(--border-strong);
}
.deliverList strong {
  display: block;
  margin-bottom: 2px;
  font-size: var(--fs-md);
  color: var(--text-strong);
}
@media (min-width: 64rem) {
  .deliverPanel {
    grid-template-columns: minmax(0, 7fr) minmax(0, 5fr);
    align-items: start;
    gap: var(--sp-16);
  }
}

/* The program tab's picture: the priced countermeasure program as a table. */
.program {
  padding: var(--sp-6);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  background: var(--surface-raised);
  box-shadow: var(--shadow-lg);
  font-variant-numeric: tabular-nums;
}
.programHead {
  margin: 0 0 var(--sp-4);
  font-weight: 600;
  color: var(--text-strong);
}
.program table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--fs-md);
}
.program th {
  padding: 0 0 var(--sp-2);
  font-size: var(--fs-sm);
  font-weight: 500;
  text-align: left;
  color: var(--text-muted);
}
.program td {
  padding: var(--sp-3) 0;
  border-top: 1px solid var(--border);
  color: var(--text-strong);
}
.program th:not(:first-child),
.program td:not(:first-child) {
  text-align: right;
}
.programFoot {
  margin: var(--sp-4) 0 0;
  font-size: 0.75rem;
  color: var(--text-muted);
}

/* ---------- Designs, not only scans: the pitch beside a design model's driver view ---------- */
.designsSection {
  /* Jumped to from the hero: stop clear of the sticky header. */
  scroll-margin-top: 8rem;
}
.designs {
  display: grid;
  gap: var(--sp-12);
  align-items: center;
}
.designsTitle {
  margin-bottom: var(--sp-5);
}
.designsIntro > p {
  margin: 0;
  max-width: 40rem;
  font-size: var(--fs-md);
  color: var(--text);
}
.designsList {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 15rem), 1fr));
  gap: var(--sp-6) var(--sp-8);
  margin: var(--sp-10) 0 0;
}
.designsList li {
  padding-top: var(--sp-4);
  border-top: 1px solid var(--border-strong);
}
.designsList h3 {
  margin-bottom: var(--sp-2);
  font-size: var(--fs-md);
}
.designsList p {
  margin: 0;
  font-size: var(--fs-sm);
  color: var(--text);
}
.designsIntro > .designsFoot {
  margin-top: var(--sp-8);
  font-size: var(--fs-sm);
  color: var(--text-muted);
}
.designsMedia {
  position: relative;
  margin: 0;
}
.designsImage {
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}
/* What the image shows, as a chip on it, like the product loops. */
.designsCaption {
  position: absolute;
  left: var(--sp-4);
  bottom: var(--sp-4);
  max-width: calc(100% - 2 * var(--sp-4));
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: var(--radius);
  background: rgba(8, 13, 19, 0.72);
  -webkit-backdrop-filter: blur(8px);
  backdrop-filter: blur(8px);
  color: #f5f7fa;
  font-size: var(--fs-sm);
  font-weight: 500;
}
@media (min-width: 64rem) {
  .designs {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: var(--sp-16);
  }
}

/* ---------- Platform ---------- */
.platform {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: var(--sp-8);
}
.platform li {
  padding-top: var(--sp-5);
  border-top: 1px solid var(--border-strong);
}
.platform h3 {
  margin-bottom: var(--sp-2);
  font-size: var(--fs-lg);
}
.platform p {
  margin: 0;
  color: var(--text);
}

@media (max-width: 63.99rem) {
  .platform {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 47.99rem) {
  .platform {
    grid-template-columns: 1fr;
  }
}
